import React, { useEffect, useState } from "react";
import { Save, X } from "lucide-react";

const FEATURES = [
  "4 Core CPU, 16 GB RAM, 1 TB SSD",
  "Supports 5G, WiFi 6",
  "20 ms Latency, 99.999% Uptime",
  "AI-Powered Network Optimization",
  "Edge Computing Capabilities",
  "16 Core CPU, 128 GB RAM, 8 TB SSD",
  "32 Core CPU, 256 GB RAM, 16 TB SSD"
];

export default function RANForm({
  onSubmit,
  isLoading,
  submitLabel = "Create RAN",
  defaultValues = null
}) {
  const [hardwareFingerprint, setHardwareFingerprint] = useState("");
  const [features, setFeatures] = useState([]);
  const [errors, setErrors] = useState({});

  const isEdit = Boolean(defaultValues);

  useEffect(() => {
    if (defaultValues) {
      setHardwareFingerprint(defaultValues.hardwareFingerprint || "");
      setFeatures(defaultValues.features || []);
    } else {
      setHardwareFingerprint("");
      setFeatures([]);
    }
    setErrors({});
  }, [defaultValues]);

  const validate = () => {
    const e = {};

    if (!isEdit) {
      if (!/^[a-fA-F0-9]{64}$/.test(hardwareFingerprint.trim())) {
        e.hardwareFingerprint = "Hardware fingerprint must be exactly 64 hex characters";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = isEdit
      ? { features } 
      : {
        hardwareFingerprint: hardwareFingerprint.trim(),
        features
      }; 

    onSubmit(payload);
  };

  const addFeature = (feature) => {
    if (!feature || features.includes(feature)) return;
    setFeatures(prev => [...prev, feature]);
  };

  const removeFeature = (feature) => {
    setFeatures(prev => prev.filter(f => f !== feature));
  };

  const availableFeatures = FEATURES.filter(f => !features.includes(f));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* HARDWARE FINGERPRINT */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Hardware Fingerprint <span className="text-red-400">*</span>
        </label>

        <textarea
          rows={2}
          disabled={isEdit}
          value={hardwareFingerprint}
          onChange={(e) => setHardwareFingerprint(e.target.value)}
          placeholder="7c049820fb3344a0fb407ad75ab509e953e8f101bacfb1113cbcbd4d5e985ff8"
          className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-primary-500 focus:ring-0 resize-none disabled:opacity-50"
        />

        {errors.hardwareFingerprint && (
          <p className="mt-1 text-xs text-red-400">
            {errors.hardwareFingerprint}
          </p>
        )}
      </div>

      {/* FEATURES */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Features
        </label>

        {/* Selected Features Input Area */}
        <div className="min-h-[44px] w-full rounded-lg bg-gray-700 border border-gray-600 px-3 py-2 flex flex-wrap gap-2">
          {features.length === 0 && (
            <span className="text-gray-400">
              No features selected. Choose from dropdown below.
            </span>
          )}

          {features.map(feature => (
            <span
              key={feature}
              className="flex items-center gap-2 bg-gray-800 border border-gray-600 text-white px-3 py-1 rounded-full text-sm"
            >
              {feature}
              <button
                type="button"
                onClick={() => removeFeature(feature)}
                className="text-gray-400 hover:text-red-400"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>

        {/* Dropdown */}
        <select
          onChange={(e) => {
            addFeature(e.target.value);
            e.target.value = "";
          }}
          className="mt-2 w-full h-10 bg-gray-700 text-white border border-gray-600 rounded-lg px-3"
        >
          <option value="" disabled>Select feature to add</option>
          {availableFeatures.map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>

        {errors.features && (
          <p className="mt-1 text-xs text-red-400">
            {errors.features}
          </p>
        )}
      </div>

      {/* SUBMIT */}
      <button
        type="submit"
        disabled={isLoading}
        className={`w-full h-10 rounded-lg text-white font-medium flex items-center justify-center gap-2
                    ${isLoading
            ? "bg-gray-600 cursor-not-allowed"
            : "bg-primary-600 hover:bg-primary-700"
          }`}
      >
        <Save size={16} />
        {isLoading
          ? submitLabel === "Update RAN"
            ? "Updating RAN..."
            : "Creating RAN..."
          : submitLabel}
      </button>

    </form>
  );
}
